import Phaser from 'phaser';
import { Rng } from '../core/rng';
import { ALL_TERRAIN, TerrainType } from '../world/terrain';
import { DecorationType } from '../world/WorldMap';
import { TERRAIN_PALETTE } from './palette';

/**
 * TEMPORARY PLACEHOLDER ART — original, generated at boot with the Canvas 2D API.
 *
 * Produces two texture atlases with named frames:
 *   - TEX.ground:  `${terrainKey}_${variant}` diamond tiles (4 variants each)
 *   - TEX.objects: decoration + marker sprites
 *
 * Everything is drawn at ART_SCALE x resolution and displayed at 1/ART_SCALE,
 * so it stays crisp when zoomed in. Replacing this file with loaded sprite
 * sheets that expose the same frame names requires no gameplay changes.
 */
export const TEX = { ground: 'ground-atlas', objects: 'object-atlas' } as const;
export const ART_SCALE = 2;
export const TILE_VARIANTS = 4;

export interface ObjectFrameSpec {
  readonly w: number;
  readonly h: number;
  /** Anchor in the frame (0..1) that sits on the tile centre. */
  readonly originX: number;
  readonly originY: number;
}

export const DECORATION_FRAMES: Partial<Record<DecorationType, string>> = {
  [DecorationType.Pine]: 'pine',
  [DecorationType.Oak]: 'oak',
  [DecorationType.Boulder]: 'boulder',
  [DecorationType.Bush]: 'bush',
  [DecorationType.Flowers]: 'flowers',
  [DecorationType.Reeds]: 'reeds',
  [DecorationType.DeadTree]: 'deadtree',
  [DecorationType.Rubble]: 'rubble',
  [DecorationType.Infestation]: 'infestation',
};

/** World-pixel sizes of object frames (before ART_SCALE). */
export const OBJECT_SPECS: Record<string, ObjectFrameSpec> = {
  pine: { w: 44, h: 78, originX: 0.5, originY: 0.9 },
  oak: { w: 52, h: 66, originX: 0.5, originY: 0.88 },
  boulder: { w: 52, h: 38, originX: 0.5, originY: 0.72 },
  bush: { w: 26, h: 20, originX: 0.5, originY: 0.8 },
  flowers: { w: 30, h: 16, originX: 0.5, originY: 0.5 },
  reeds: { w: 26, h: 26, originX: 0.5, originY: 0.85 },
  deadtree: { w: 40, h: 64, originX: 0.5, originY: 0.9 },
  rubble: { w: 60, h: 40, originX: 0.5, originY: 0.7 },
  infestation: { w: 40, h: 22, originX: 0.5, originY: 0.5 },
};

export function generateArt(scene: Phaser.Scene, tileW: number, tileH: number): void {
  if (!scene.textures.exists(TEX.ground)) buildGroundAtlas(scene, tileW, tileH);
  if (!scene.textures.exists(TEX.objects)) buildObjectAtlas(scene);
}

// ---------------------------------------------------------------- ground

/** Plain white diamond (exact tile size, no overdraw) used for tinted overlays. */
export const OVERLAY_DIAMOND_FRAME = 'overlay_diamond';

function buildGroundAtlas(scene: Phaser.Scene, tileW: number, tileH: number): void {
  const fw = (tileW + 2) * ART_SCALE;
  const fh = (tileH + 2) * ART_SCALE;
  const cols = TILE_VARIANTS;
  const rows = ALL_TERRAIN.length + 1; // + one row for overlay frames
  const tex = scene.textures.createCanvas(TEX.ground, fw * cols, fh * rows);
  if (!tex) throw new Error('Could not create ground atlas canvas');
  const ctx = tex.getContext();

  ALL_TERRAIN.forEach((t, row) => {
    for (let v = 0; v < TILE_VARIANTS; v++) {
      const ox = v * fw;
      const oy = row * fh;
      ctx.save();
      ctx.translate(ox, oy);
      drawGroundTile(ctx, t.id, v, fw, fh, tileW * ART_SCALE, tileH * ART_SCALE);
      ctx.restore();
      tex.add(`${t.key}_${v}`, 0, ox, oy, fw, fh);
    }
  });

  const oy = ALL_TERRAIN.length * fh;
  ctx.fillStyle = '#ffffff';
  diamondPath(ctx, fw / 2, oy + fh / 2, tileW * ART_SCALE, tileH * ART_SCALE);
  ctx.fill();
  tex.add(OVERLAY_DIAMOND_FRAME, 0, 0, oy, fw, fh);

  tex.refresh();
}

function diamondPath(ctx: CanvasRenderingContext2D, cx: number, cy: number, w: number, h: number): void {
  ctx.beginPath();
  ctx.moveTo(cx, cy - h / 2);
  ctx.lineTo(cx + w / 2, cy);
  ctx.lineTo(cx, cy + h / 2);
  ctx.lineTo(cx - w / 2, cy);
  ctx.closePath();
}

function drawGroundTile(ctx: CanvasRenderingContext2D, terrain: TerrainType, variant: number, fw: number, fh: number, tw: number, th: number): void {
  const p = TERRAIN_PALETTE[terrain];
  const rng = new Rng(terrain * 7919 + variant * 104729 + 17);
  const cx = fw / 2;
  const cy = fh / 2;
  // Slight overdraw (1 art px each side) hides seams between neighbouring tiles.
  const w = tw + 2;
  const h = th + 1;

  diamondPath(ctx, cx, cy, w, h);
  ctx.save();
  ctx.clip();

  // Flat base plus a very soft light so neighbouring tiles blend instead of
  // reading as a checkerboard.
  ctx.fillStyle = p.base;
  ctx.fillRect(0, 0, fw, fh);
  const g = ctx.createLinearGradient(cx - w / 2, cy - h / 2, cx + w / 2, cy + h / 2);
  g.addColorStop(0, p.light);
  g.addColorStop(1, p.dark);
  ctx.globalAlpha = 0.18 + (variant % 2) * 0.06;
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, fw, fh);
  ctx.globalAlpha = 1;

  // Mottling.
  for (let i = 0; i < 26; i++) {
    ctx.globalAlpha = 0.14 + rng.next() * 0.16;
    ctx.fillStyle = rng.chance(0.5) ? p.light : p.dark;
    ellipse(ctx, rng.next() * fw, rng.next() * fh, 3 + rng.next() * 9, 1.5 + rng.next() * 4);
  }
  ctx.globalAlpha = 1;

  switch (terrain) {
    case TerrainType.Grass:
    case TerrainType.Meadow:
    case TerrainType.Forest: {
      const blades = terrain === TerrainType.Forest ? 22 : 40;
      ctx.lineWidth = 1.4;
      for (let i = 0; i < blades; i++) {
        const x = rng.next() * fw;
        const y = rng.next() * fh;
        ctx.strokeStyle = rng.chance(0.6) ? p.accent : p.dark;
        ctx.globalAlpha = 0.55;
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(x + (rng.next() - 0.5) * 3, y - 3 - rng.next() * 4);
        ctx.stroke();
      }
      if (terrain === TerrainType.Meadow) {
        for (let i = 0; i < 8; i++) {
          ctx.globalAlpha = 0.85;
          ctx.fillStyle = rng.chance(0.5) ? '#f3e7a1' : '#e9e3d6';
          ellipse(ctx, rng.next() * fw, rng.next() * fh, 1.6, 1.1);
        }
      }
      break;
    }
    case TerrainType.Dirt:
    case TerrainType.Road: {
      for (let i = 0; i < 12; i++) {
        ctx.globalAlpha = 0.7;
        ctx.fillStyle = rng.chance(0.5) ? p.accent : p.light;
        ellipse(ctx, rng.next() * fw, rng.next() * fh, 1.5 + rng.next() * 2, 1 + rng.next());
      }
      if (terrain === TerrainType.Road) {
        // Cracks in old asphalt.
        ctx.globalAlpha = 0.55;
        ctx.strokeStyle = p.accent;
        ctx.lineWidth = 1.2;
        for (let k = 0; k < 3; k++) {
          let x = rng.next() * fw;
          let y = rng.next() * fh;
          ctx.beginPath();
          ctx.moveTo(x, y);
          for (let j = 0; j < 3; j++) {
            x += (rng.next() - 0.5) * 16;
            y += (rng.next() - 0.5) * 8;
            ctx.lineTo(x, y);
          }
          ctx.stroke();
        }
        // Faded lane paint, broken.
        if (variant % 2 === 0) {
          ctx.globalAlpha = 0.35;
          ctx.fillStyle = '#c9b46a';
          ctx.fillRect(cx - 6, cy - 1, 12, 2);
        }
      }
      break;
    }
    case TerrainType.Ruins: {
      // Broken concrete slabs.
      for (let i = 0; i < 6; i++) {
        ctx.globalAlpha = 0.6;
        ctx.fillStyle = rng.chance(0.5) ? p.light : p.dark;
        const x = rng.next() * fw;
        const y = rng.next() * fh;
        ctx.fillRect(x, y, 6 + rng.next() * 10, 3 + rng.next() * 4);
      }
      break;
    }
    case TerrainType.Rock: {
      ctx.globalAlpha = 0.5;
      ctx.strokeStyle = p.accent;
      ctx.lineWidth = 1.5;
      for (let i = 0; i < 4; i++) {
        let x = rng.next() * fw;
        let y = rng.next() * fh;
        ctx.beginPath();
        ctx.moveTo(x, y);
        for (let k = 0; k < 3; k++) {
          x += (rng.next() - 0.3) * 14;
          y += (rng.next() - 0.5) * 7;
          ctx.lineTo(x, y);
        }
        ctx.stroke();
      }
      break;
    }
    case TerrainType.Water: {
      ctx.lineWidth = 1.6;
      ctx.strokeStyle = p.accent;
      for (let i = 0; i < 5; i++) {
        const x = rng.next() * fw;
        const y = rng.next() * fh;
        const len = 8 + rng.next() * 14;
        ctx.globalAlpha = 0.35 + rng.next() * 0.3;
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.quadraticCurveTo(x + len / 2, y - 2, x + len, y);
        ctx.stroke();
      }
      break;
    }
    default:
      break;
  }
  ctx.restore();
  ctx.globalAlpha = 1;
}

function ellipse(ctx: CanvasRenderingContext2D, x: number, y: number, rx: number, ry: number): void {
  ctx.beginPath();
  ctx.ellipse(x, y, rx, ry, 0, 0, Math.PI * 2);
  ctx.fill();
}

// ---------------------------------------------------------------- objects

function buildObjectAtlas(scene: Phaser.Scene): void {
  const names = Object.keys(OBJECT_SPECS);
  const pad = 2;
  let width = 0;
  let height = 0;
  for (const n of names) {
    const s = OBJECT_SPECS[n]!;
    width += s.w * ART_SCALE + pad;
    height = Math.max(height, s.h * ART_SCALE);
  }
  const tex = scene.textures.createCanvas(TEX.objects, width, height);
  if (!tex) throw new Error('Could not create object atlas canvas');
  const ctx = tex.getContext();
  let x = 0;
  for (const n of names) {
    const s = OBJECT_SPECS[n]!;
    const fw = s.w * ART_SCALE;
    const fh = s.h * ART_SCALE;
    ctx.save();
    ctx.translate(x, 0);
    ctx.scale(ART_SCALE, ART_SCALE);
    drawObject(ctx, n, s);
    ctx.restore();
    tex.add(n, 0, x, 0, fw, fh);
    x += fw + pad;
  }
  tex.refresh();
}

function shadow(ctx: CanvasRenderingContext2D, cx: number, cy: number, rx: number, ry: number): void {
  ctx.save();
  ctx.globalAlpha = 0.28;
  ctx.fillStyle = '#10180c';
  ellipse(ctx, cx, cy, rx, ry);
  ctx.restore();
}

function drawObject(ctx: CanvasRenderingContext2D, name: string, s: ObjectFrameSpec): void {
  const bx = s.w * s.originX;
  const by = s.h * s.originY;
  switch (name) {
    case 'pine': {
      shadow(ctx, bx + 3, by, 15, 6);
      ctx.fillStyle = '#5a3d22';
      ctx.fillRect(bx - 2.5, by - 14, 5, 14);
      const layers = [
        { y: by - 10, w: 40, h: 26 },
        { y: by - 26, w: 32, h: 24 },
        { y: by - 42, w: 24, h: 22 },
        { y: by - 55, w: 15, h: 18 },
      ];
      for (const l of layers) {
        ctx.fillStyle = '#2f5a2c';
        triangle(ctx, bx, l.y - l.h, bx - l.w / 2, l.y, bx + l.w / 2, l.y);
        ctx.fillStyle = '#3f7537';
        triangle(ctx, bx, l.y - l.h, bx - l.w / 2, l.y, bx - 1, l.y);
        ctx.fillStyle = 'rgba(255,255,255,0.08)';
        triangle(ctx, bx, l.y - l.h, bx - l.w / 4, l.y - l.h / 2, bx - 1, l.y - l.h / 3);
      }
      break;
    }
    case 'oak': {
      shadow(ctx, bx + 4, by, 19, 7);
      ctx.fillStyle = '#5e4026';
      ctx.fillRect(bx - 3, by - 20, 6, 20);
      const blobs: [number, number, number, string][] = [
        [-11, -30, 12, '#3d6a2c'], [11, -30, 12, '#365f27'], [0, -40, 14, '#44762f'],
        [-6, -24, 11, '#3a6529'], [7, -23, 10, '#335a24'], [-3, -47, 9, '#4f8536'],
      ];
      for (const [dx, dy, r, c] of blobs) {
        ctx.fillStyle = c;
        ctx.beginPath();
        ctx.arc(bx + dx, by + dy, r, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.fillStyle = 'rgba(200,230,140,0.18)';
      ctx.beginPath();
      ctx.arc(bx - 6, by - 44, 7, 0, Math.PI * 2);
      ctx.fill();
      break;
    }
    case 'boulder': {
      shadow(ctx, bx + 2, by + 2, 22, 7);
      ctx.fillStyle = '#7d7d76';
      poly(ctx, [[bx - 21, by], [bx - 15, by - 17], [bx - 2, by - 25], [bx + 13, by - 19], [bx + 22, by - 4], [bx + 10, by + 5], [bx - 10, by + 5]]);
      ctx.fillStyle = '#9d9d94';
      poly(ctx, [[bx - 15, by - 17], [bx - 2, by - 25], [bx + 13, by - 19], [bx + 2, by - 10], [bx - 10, by - 8]]);
      ctx.fillStyle = '#5f5f59';
      poly(ctx, [[bx + 2, by - 10], [bx + 13, by - 19], [bx + 22, by - 4], [bx + 10, by + 5]]);
      break;
    }
    case 'bush': {
      shadow(ctx, bx + 1, by, 10, 4);
      for (const [dx, dy, r, c] of [[-5, -5, 6, '#3c6a2b'], [5, -5, 6, '#355f26'], [0, -9, 6, '#4a7d33']] as const) {
        ctx.fillStyle = c;
        ctx.beginPath();
        ctx.arc(bx + dx, by + dy, r, 0, Math.PI * 2);
        ctx.fill();
      }
      break;
    }
    case 'flowers': {
      const rng = new Rng(4242);
      for (let i = 0; i < 9; i++) {
        ctx.fillStyle = ['#f1d14a', '#efe9e0', '#d9708a', '#b48ee0'][i % 4]!;
        ellipse(ctx, bx + (rng.next() - 0.5) * 24, by + (rng.next() - 0.5) * 10, 1.6, 1.3);
      }
      break;
    }
    case 'reeds': {
      ctx.strokeStyle = '#6f8a3a';
      ctx.lineWidth = 1.4;
      for (let i = -3; i <= 3; i++) {
        ctx.beginPath();
        ctx.moveTo(bx + i * 2.5, by);
        ctx.quadraticCurveTo(bx + i * 3.2, by - 10, bx + i * 4, by - 16 - (i % 2) * 4);
        ctx.stroke();
      }
      ctx.fillStyle = '#5c4126';
      for (const i of [-2, 1, 3]) ellipse(ctx, bx + i * 4, by - 17 - (i % 2) * 4, 1.4, 3);
      break;
    }
    case 'deadtree': {
      shadow(ctx, bx + 3, by, 11, 4);
      ctx.strokeStyle = '#4a3d31';
      ctx.lineCap = 'round';
      ctx.lineWidth = 4;
      ctx.beginPath();
      ctx.moveTo(bx, by);
      ctx.lineTo(bx + 1, by - 42);
      ctx.stroke();
      ctx.lineWidth = 2.2;
      const branches: [number, number, number, number][] = [
        [bx, by - 20, bx - 12, by - 34], [bx + 1, by - 28, bx + 13, by - 44],
        [bx + 1, by - 38, bx - 8, by - 52], [bx - 6, by - 28, bx - 15, by - 31],
        [bx + 7, by - 37, bx + 16, by - 38],
      ];
      for (const [x1, y1, x2, y2] of branches) {
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.stroke();
      }
      break;
    }
    case 'rubble': {
      shadow(ctx, bx + 2, by + 2, 24, 7);
      // Broken wall stub with exposed rebar.
      ctx.fillStyle = '#857f75';
      poly(ctx, [[bx - 20, by - 2], [bx - 20, by - 26], [bx - 12, by - 30], [bx - 6, by - 22], [bx - 2, by - 27], [bx + 2, by - 6]]);
      ctx.fillStyle = '#6a655d';
      poly(ctx, [[bx + 2, by - 6], [bx - 2, by - 27], [bx + 4, by - 24], [bx + 6, by - 3]]);
      ctx.strokeStyle = '#7a4a2e';
      ctx.lineWidth = 1.2;
      for (const dx of [-15, -9]) {
        ctx.beginPath();
        ctx.moveTo(bx + dx, by - 27);
        ctx.lineTo(bx + dx + 2, by - 36);
        ctx.stroke();
      }
      // Debris chunks.
      for (const [dx, dy, r, c] of [[10, -2, 6, '#8f897f'], [17, 1, 4, '#6f6a62'], [-6, 3, 4, '#7c776e'], [5, 5, 3, '#5f5b54']] as const) {
        ctx.fillStyle = c;
        poly(ctx, [[bx + dx - r, by + dy], [bx + dx - r / 2, by + dy - r], [bx + dx + r, by + dy - r / 2], [bx + dx + r / 2, by + dy + r / 2]]);
      }
      break;
    }
    case 'infestation': {
      const rng = new Rng(666);
      for (let i = 0; i < 7; i++) {
        ctx.globalAlpha = 0.55 + rng.next() * 0.3;
        ctx.fillStyle = i % 3 === 0 ? '#5c1520' : '#3a1016';
        ellipse(ctx, bx + (rng.next() - 0.5) * 30, by + (rng.next() - 0.5) * 12, 3 + rng.next() * 6, 1.5 + rng.next() * 3);
      }
      ctx.globalAlpha = 1;
      break;
    }
    default:
      throw new Error(`No drawing routine for object frame "${name}"`);
  }
}

function triangle(ctx: CanvasRenderingContext2D, x1: number, y1: number, x2: number, y2: number, x3: number, y3: number): void {
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.lineTo(x3, y3);
  ctx.closePath();
  ctx.fill();
}

function poly(ctx: CanvasRenderingContext2D, pts: readonly (readonly [number, number])[]): void {
  ctx.beginPath();
  pts.forEach(([x, y], i) => (i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y)));
  ctx.closePath();
  ctx.fill();
}
